import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Target, Save } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { fmtNum } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { canEditModule } from "@/lib/roles";

const inr = (n) => (n == null ? "—" : `₹${fmtNum(Math.round(n))}`);

export function MvaTargetEditor({ masters, plantId, reportingDate }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const canEdit = canEditModule(user, "dashboard");
  const sections = masters.sections.filter((s) => s.active && (!plantId || s.plant_id === plantId));
  const q = useQuery({
    queryKey: ["mva-targets", reportingDate, plantId],
    queryFn: async () => (await api.get("/mva-targets", { params: { reporting_date: reportingDate, plant_id: plantId || undefined } })).data,
  });
  useEffect(() => { if (q.data) setVals(Object.fromEntries(q.data.map((t) => [t.section_id, String(t.mva_target)]))); }, [q.data]);

  const save = async () => {
    setBusy(true);
    try {
      await Promise.all(sections.filter((s) => vals[s.id] !== undefined && vals[s.id] !== "").map((s) =>
        api.put("/mva-targets", { plant_id: s.plant_id, section_id: s.id, reporting_date: reportingDate, mva_target: Number(vals[s.id]) })));
      toast.success("Daily MVA targets saved");
      qc.invalidateQueries({ queryKey: ["mva-targets"] }); qc.invalidateQueries({ queryKey: ["dashboard"] });
      setOpen(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!canEdit) return null;
  return (
    <div className="relative">
      <button className="pp-btn-ghost h-9 px-3" onClick={() => setOpen(!open)} data-testid="mva-target-toggle"><Target size={14} /> Daily targets</button>
      {open && (
        <div className="absolute right-0 z-20 mt-2 w-80 pp-card p-4 space-y-3 shadow-xl rise" data-testid="mva-target-editor">
          <div className="pp-label">Daily MVA target · {reportingDate}</div>
          {sections.map((s) => (
            <label key={s.id} className="flex items-center gap-3 text-sm">
              <span className="w-16 font-mono font-semibold text-blue-700">{s.code}</span>
              <input type="number" min="0" className="pp-input h-9 font-mono" placeholder="₹ target" value={vals[s.id] ?? ""} onChange={(e) => setVals({ ...vals, [s.id]: e.target.value })} data-testid={`mva-target-input-${s.code}`} />
            </label>
          ))}
          <button className="pp-btn-primary w-full h-9" disabled={busy} onClick={save} data-testid="mva-target-save"><Save size={14} /> {busy ? "Saving…" : "Save targets"}</button>
        </div>
      )}
    </div>
  );
}

export function MvaGapTable({ rows }) {
  const withTarget = rows.filter((r) => r.daily_target);
  if (!withTarget.length) return <div className="text-xs text-slate-500 mt-3" data-testid="mva-gap-empty">No daily MVA targets set for this day — management can set them via "Daily targets".</div>;
  return (
    <div className="overflow-x-auto mt-3"><table className="w-full text-xs border-collapse" data-testid="mva-gap-table">
      <thead><tr className="text-left pp-label border-b border-slate-200"><th className="py-1.5 pr-2">Section</th><th className="py-1.5 pr-2 text-right">Earned</th><th className="py-1.5 pr-2 text-right">Daily target</th><th className="py-1.5 pr-2 text-right">Gap</th><th className="py-1.5 text-right">% of target</th></tr></thead>
      <tbody>
        {withTarget.map((r) => (
          <tr key={r.name} className="border-b border-slate-100" data-testid={`mva-gap-${r.name}`}>
            <td className="py-1.5 pr-2 font-semibold">{r.name}</td>
            <td className="py-1.5 pr-2 text-right font-mono">{inr(r.mva_earned)}</td>
            <td className="py-1.5 pr-2 text-right font-mono">{inr(r.daily_target)}</td>
            <td className={`py-1.5 pr-2 text-right font-mono font-semibold ${r.gap > 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.gap > 0 ? `-${inr(r.gap)}` : `+${inr(-r.gap)}`}</td>
            <td className={`py-1.5 text-right font-mono ${r.daily_pct >= 95 ? "text-emerald-600" : r.daily_pct >= 80 ? "text-amber-600" : "text-rose-600"}`}>{r.daily_pct}%</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}
